import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { CommercialRoutePlan, GeneratedPlansMap, SurgeonRoutePlan } from '@lp/features/RoutePlanner/types';
import { convertGroups, createGroupGrowthTracker, isAbortError, pollTask } from '@lp/features/RoutePlanner/utils';
import { Case } from '@/api/apis/case';
import {
  PlanActionTeamKeyword,
  PlanType,
  RouteTypeSettingsEnum,
  TeamResourcesByKeyword,
} from '@/api/apis/mission-planner';
import { retrieveUnifiedError } from '@/api/utils';
import { RoutePlanType } from '@/types/route';
import {
  convertCommercialFlights,
  convertSurgeonTrips,
  getDefaultDryRunSegments,
  getDefaultSegments,
  getRoutePlan,
} from '@/utils/route';
import { suggestedQueries } from './suggestedQueries';

const isTaskCompleted = ({ status }: { status: string }) => status === 'COMPLETED';
const getTaskErrors = ({ errors }: { errors: string[] }) => errors;
const isTaskRunning = ({ status }: { status: string }) => status === 'RUNNING' || status === 'NOTSTARTED';

const firstWarning = (warnings?: string[]): string | null => warnings?.[0] ?? null;

const getPlanIdByGroupIndex = (basePlans: GeneratedPlansMap, groupIndex: number): string | undefined =>
  Object.values(basePlans).find((p) => p.index === groupIndex)?.id;

const grownCommercialGroups = (basePlans: GeneratedPlansMap, grownIndexes: number[]): number[] =>
  grownIndexes.filter((groupIndex) => !!getPlanIdByGroupIndex(basePlans, groupIndex));

const getPlanTeamResources = (
  basePlans: GeneratedPlansMap,
  groupIndex: number,
  planIndex: number,
): Partial<TeamResourcesByKeyword> => {
  const planId = getPlanIdByGroupIndex(basePlans, groupIndex);

  return planId ? (basePlans[planId].routes[planIndex]?.teamResources ?? {}) : {};
};

type ConvertRouteParams = {
  route: PlanType;
  planType: RouteTypeSettingsEnum;
  isTmdxDriveCompatible: boolean;
};

type PollParams = {
  planId: string;
  isTmdxDriveCompatible: boolean;
  signal?: AbortSignal;
};

type StreamedCommercialParams = PollParams & {
  basePlans: GeneratedPlansMap;
  onProgress: (groups: Record<number, CommercialRoutePlan[]>) => void;
};

export type WarnedResult<T> = {
  routes: T;
  warning: string | null;
};

export type UseConvertSuggestedRoutesType = {
  convertRoute: (params: ConvertRouteParams) => Promise<RoutePlanType>;
  getBenchmarkRoute: (params: PollParams) => Promise<RoutePlanType | null>;
  getSuggestedRoutes: (
    params: PollParams & { planType: RouteTypeSettingsEnum },
  ) => Promise<WarnedResult<RoutePlanType[][]>>;
  getCommercialFlightsRoutes: (params: StreamedCommercialParams) => Promise<void>;
  getSuggestedSurgeonRoutes: (
    params: PollParams & { onProgress: (groups: Record<number, (SurgeonRoutePlan | null)[]>) => void },
  ) => Promise<string | null>;
  getPartialRoutes: (
    params: PollParams & {
      planType: RouteTypeSettingsEnum;
      teamResources: Pick<TeamResourcesByKeyword, PlanActionTeamKeyword.team | PlanActionTeamKeyword.teamWithoutOcs>;
    },
  ) => Promise<WarnedResult<RoutePlanType[]>>;
  getPartialCommercialFlightsRoutes: (params: StreamedCommercialParams) => Promise<void>;
};

export const useConvertSuggestedRoutes = (): UseConvertSuggestedRoutesType => {
  const { donorHospitalDetails: donorHospital, txCenterDetails: txCenter } = useCaseDetailsWithReviews() as Case;

  const defaultSegments = useMemo(
    () => getDefaultSegments({ donorHospital: donorHospital!, txCenter }),
    [donorHospital, txCenter],
  );
  const dryRunDefaultSegments = useMemo(() => getDefaultDryRunSegments(donorHospital!), [donorHospital]);

  const convertRoute = useCallback(
    async ({ route, planType, isTmdxDriveCompatible }: ConvertRouteParams): Promise<RoutePlanType> =>
      getRoutePlan({
        txCenter,
        donorHospital,
        plan: route,
        defaultSegments,
        dryRunDefaultSegments,
        planType,
        isTmdxDriveCompatible,
      }),
    [txCenter, donorHospital, defaultSegments, dryRunDefaultSegments],
  );

  const getBenchmarkRoute = useCallback(
    async ({ planId, isTmdxDriveCompatible, signal }: PollParams) => {
      try {
        const result = await pollTask({
          fetch: () => suggestedQueries.getBenchmarkPlanById(planId),
          isCompleted: ({ benchmarkPlans }) => isTaskCompleted(benchmarkPlans),
          getErrors: ({ benchmarkPlans }) => benchmarkPlans.errors,
          signal,
        });

        return result
          ? await convertRoute({
              route: result.benchmarkPlans.items[0],
              planType: RouteTypeSettingsEnum.Full,
              isTmdxDriveCompatible,
            })
          : null;
      } catch (e) {
        if (isAbortError(e)) throw e;
        toast.error(retrieveUnifiedError(e).message);

        return null;
      }
    },
    [convertRoute],
  );

  const getSuggestedRoutes = useCallback(
    async ({ planId, planType, isTmdxDriveCompatible, signal }: PollParams & { planType: RouteTypeSettingsEnum }) => {
      try {
        const result = await pollTask({
          fetch: () => suggestedQueries.getRoutePlansById(planId),
          isCompleted: ({ plans }) => isTaskCompleted(plans),
          getErrors: ({ plans }) => plans.errors,
          isRunning: ({ plans }) => isTaskRunning(plans),
          signal,
        });

        if (!result) return { routes: [], warning: null };

        const routes = await Promise.all(
          result.plans.items.map(async (p) =>
            Promise.all(p.map((route) => convertRoute({ route, planType, isTmdxDriveCompatible }))),
          ),
        );

        return { routes, warning: firstWarning(result.plans.warnings) };
      } catch (e) {
        if (isAbortError(e)) throw e;
        toast.error(retrieveUnifiedError(e).message);

        return { routes: [], warning: null };
      }
    },
    [convertRoute],
  );

  const getPartialRoutes = useCallback(
    async ({
      planId,
      planType,
      teamResources,
      isTmdxDriveCompatible,
      signal,
    }: PollParams & {
      planType: RouteTypeSettingsEnum;
      teamResources: Pick<TeamResourcesByKeyword, PlanActionTeamKeyword.team | PlanActionTeamKeyword.teamWithoutOcs>;
    }) => {
      try {
        const result = await pollTask({
          fetch: () => suggestedQueries.getPartialRoutePlanById(planId),
          isCompleted: ({ plans }) => isTaskCompleted(plans),
          getErrors: ({ plans }) => plans.errors,
          isRunning: ({ plans }) => isTaskRunning(plans),
          signal,
        });

        if (!result) return { routes: [], warning: null };

        const routes = await Promise.all(
          result.plans.items.map(async (r) =>
            convertRoute({ route: { ...r, ...teamResources }, planType, isTmdxDriveCompatible }),
          ),
        );

        return { routes, warning: firstWarning(result.plans.warnings) };
      } catch (e) {
        if (isAbortError(e)) throw e;
        toast.error(retrieveUnifiedError(e).message);

        return { routes: [], warning: null };
      }
    },
    [convertRoute],
  );

  const getSuggestedSurgeonRoutes = useCallback(
    async ({
      planId,
      isTmdxDriveCompatible,
      onProgress,
      signal,
    }: PollParams & { onProgress: (groups: Record<number, (SurgeonRoutePlan | null)[]>) => void }) => {
      const trackGrowth = createGroupGrowthTracker();

      try {
        const result = await pollTask({
          fetch: () => suggestedQueries.getSurgeonRoutesByPlanId(planId),
          isCompleted: isTaskCompleted,
          getErrors: getTaskErrors,
          signal,
          onProgress: async ({ surgeonTrips2D }) => {
            const converted = await convertGroups(trackGrowth(surgeonTrips2D), surgeonTrips2D, (trip) =>
              convertSurgeonTrips(trip, isTmdxDriveCompatible),
            );

            if (converted) onProgress(converted);
          },
        });

        return firstWarning(result?.warnings);
      } catch (e) {
        if (isAbortError(e)) throw e;
        toast.error(retrieveUnifiedError(e).message);

        return null;
      }
    },
    [],
  );

  const getCommercialFlightsRoutes = useCallback(
    async ({ planId, basePlans, isTmdxDriveCompatible, onProgress, signal }: StreamedCommercialParams) => {
      const trackGrowth = createGroupGrowthTracker();

      try {
        await pollTask({
          fetch: () => suggestedQueries.getCommercialFlightsById(planId),
          isCompleted: isTaskCompleted,
          getErrors: getTaskErrors,
          signal,
          onProgress: async ({ planCommercialFlights2D }) => {
            const converted = await convertGroups(
              grownCommercialGroups(basePlans, trackGrowth(planCommercialFlights2D)),
              planCommercialFlights2D,
              async (flights, groupIndex, planIndex) =>
                convertCommercialFlights(
                  flights,
                  getPlanTeamResources(basePlans, groupIndex, planIndex),
                  isTmdxDriveCompatible,
                ),
            );

            if (converted) onProgress(converted);
          },
        });
      } catch (e) {
        if (isAbortError(e)) throw e;
        toast.error(retrieveUnifiedError(e).message);
      }
    },
    [],
  );

  const getPartialCommercialFlightsRoutes = useCallback(
    async ({ planId, basePlans, isTmdxDriveCompatible, onProgress, signal }: StreamedCommercialParams) => {
      const trackGrowth = createGroupGrowthTracker();

      try {
        await pollTask({
          fetch: () => suggestedQueries.getPartialCommercialFlightsById(planId),
          isCompleted: isTaskCompleted,
          getErrors: getTaskErrors,
          signal,
          onProgress: async ({ planCommercialFlights }) => {
            // A partial generation returns a single flat list, which is the only group there is.
            const groups = [planCommercialFlights];
            const converted = await convertGroups(
              grownCommercialGroups(basePlans, trackGrowth(groups)),
              groups,
              async (flights, _groupIndex, planIndex) =>
                convertCommercialFlights(flights, getPlanTeamResources(basePlans, 0, planIndex), isTmdxDriveCompatible),
            );

            if (converted) onProgress(converted);
          },
        });
      } catch (e) {
        if (isAbortError(e)) throw e;
        toast.error(retrieveUnifiedError(e).message);
      }
    },
    [],
  );

  return {
    convertRoute,
    getBenchmarkRoute,
    getSuggestedRoutes,
    getSuggestedSurgeonRoutes,
    getCommercialFlightsRoutes,
    getPartialRoutes,
    getPartialCommercialFlightsRoutes,
  };
};
